import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase, uploadFile } from '../lib/supabase'
import { DashboardLayout } from '../components/Layout'
import { Button, Input, Select, Textarea, Badge, toast, Spinner, EmptyState, Modal, FileUpload, ConfirmDialog } from '../components/UI'
import { Plus, Edit2, Trash2, Eye, MapPin, Building2, ToggleLeft, ToggleRight } from 'lucide-react'

const AD_TYPES = [
  { value: 'billboard', label: 'Billboard' },
  { value: 'digital_screen', label: 'Digital Screen' },
  { value: 'bus_shelter', label: 'Bus Shelter' },
  { value: 'unipole', label: 'Unipole' },
  { value: 'lamp_post', label: 'Lamp Post' },
  { value: 'building_wrap', label: 'Building Wrap' },
  { value: 'mall_display', label: 'Mall Display' },
  { value: 'other', label: 'Other' },
]

const AREAS = ['Sheikh Zayed Road', 'Dubai Marina', 'JLT', 'Business Bay', 'DIFC', 'Deira', 'Bur Dubai', 'JBR', 'Palm Jumeirah', 'Downtown Dubai', 'Al Barsha', 'Jumeirah', 'Mirdif', 'Al Qusais', 'Other']

function SpaceForm({ initial, onSave, onClose }) {
  const { profile } = useAuth()
  const [form, setForm] = useState(initial || {
    title: '', description: '', type: 'billboard',
    latitude: '', longitude: '', address: '', city: 'Dubai', area: '',
    width_meters: '', height_meters: '', is_digital: false, is_illuminated: true, faces_direction: '',
    daily_impressions: '', price_per_day: '', price_per_week: '', price_per_month: '',
    minimum_booking_days: '7', production_fee: '0',
    audience_type: [], nearby_landmarks: '', commission_rate: '10',
  })
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  function validate() {
    const e = {}
    if (!form.title) e.title = 'Title required'
    if (!form.type) e.type = 'Type required'
    if (!form.latitude || isNaN(form.latitude)) e.latitude = 'Valid latitude required'
    if (!form.longitude || isNaN(form.longitude)) e.longitude = 'Valid longitude required'
    if (!form.area) e.area = 'Area required'
    if (!form.price_per_month) e.price_per_month = 'Monthly price required'
    return e
  }

  async function handleSave() {
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    setLoading(true)
    try {
      // Upload photos
      let photoUrls = form.photos || []
      for (const file of photos) {
        const path = `${profile.id}/${Date.now()}-${file.name}`
        const url = await uploadFile('ad-space-photos', path, file)
        photoUrls = [...photoUrls, url]
      }

      const payload = {
        ...form,
        owner_id: profile.id,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        width_meters: form.width_meters ? parseFloat(form.width_meters) : null,
        height_meters: form.height_meters ? parseFloat(form.height_meters) : null,
        daily_impressions: form.daily_impressions ? parseInt(form.daily_impressions) : null,
        price_per_day: form.price_per_day ? parseFloat(form.price_per_day) : null,
        price_per_week: form.price_per_week ? parseFloat(form.price_per_week) : null,
        price_per_month: parseFloat(form.price_per_month),
        minimum_booking_days: parseInt(form.minimum_booking_days) || 7,
        production_fee: parseFloat(form.production_fee) || 0,
        commission_rate: parseFloat(form.commission_rate) || 10,
        photos: photoUrls,
        nearby_landmarks: form.nearby_landmarks ? form.nearby_landmarks.split('\n').filter(Boolean) : [],
      }

      if (initial?.id) {
        const { error } = await supabase.from('ad_spaces').update(payload).eq('id', initial.id)
        if (error) throw error
        toast.success('Ad space updated!')
      } else {
        const { error } = await supabase.from('ad_spaces').insert(payload)
        if (error) throw error
        toast.success('Ad space listed successfully!')
      }
      onSave()
    } catch (e) {
      toast.error(e.message || 'Failed to save ad space')
    } finally {
      setLoading(false)
    }
  }

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Basic Info */}
      <section>
        <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Basic Information
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Space Title" placeholder="e.g. Sheikh Zayed Road - North Facing Unipole" required
            value={form.title} onChange={e => set('title', e.target.value)} error={errors.title} />
          <Textarea label="Description" placeholder="Describe the space, its visibility, traffic..." rows={3}
            value={form.description} onChange={e => set('description', e.target.value)} />
          <div className="grid-2">
            <Select label="Type" required value={form.type} onChange={e => set('type', e.target.value)} error={errors.type}>
              {AD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
            <Select label="Area" required value={form.area} onChange={e => set('area', e.target.value)} error={errors.area}>
              <option value="">Select area...</option>
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </Select>
          </div>
          <Input label="Full Address" placeholder="Street, Building, Landmark" value={form.address}
            onChange={e => set('address', e.target.value)} />
        </div>
      </section>

      {/* Location */}
      <section>
        <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          GPS Coordinates
        </h4>
        <div className="grid-2">
          <Input label="Latitude" type="number" step="0.000001" placeholder="25.2048" required
            value={form.latitude} onChange={e => set('latitude', e.target.value)} error={errors.latitude}
            hint="Tip: Right-click on Google Maps → 'What's here?'" />
          <Input label="Longitude" type="number" step="0.000001" placeholder="55.2708" required
            value={form.longitude} onChange={e => set('longitude', e.target.value)} error={errors.longitude} />
        </div>
      </section>

      {/* Dimensions */}
      <section>
        <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Physical Specs
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          <Input label="Width (meters)" type="number" step="0.1" placeholder="12"
            value={form.width_meters} onChange={e => set('width_meters', e.target.value)} />
          <Input label="Height (meters)" type="number" step="0.1" placeholder="4"
            value={form.height_meters} onChange={e => set('height_meters', e.target.value)} />
          <Select label="Facing Direction" value={form.faces_direction} onChange={e => set('faces_direction', e.target.value)}>
            <option value="">N/A</option>
            {['North', 'South', 'East', 'West', 'Northeast', 'Northwest', 'Southeast', 'Southwest'].map(d => <option key={d}>{d}</option>)}
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 24, marginTop: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_digital} onChange={e => set('is_digital', e.target.checked)}
              style={{ width: 16, height: 16, accentColor: 'var(--tech)' }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>Digital / LED Screen</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_illuminated} onChange={e => set('is_illuminated', e.target.checked)}
              style={{ width: 16, height: 16, accentColor: 'var(--tech)' }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>Illuminated at Night</span>
          </label>
        </div>
      </section>

      {/* Pricing */}
      <section>
        <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Pricing (AED)
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          <Input label="Monthly Price" type="number" placeholder="10,000" required
            value={form.price_per_month} onChange={e => set('price_per_month', e.target.value)} error={errors.price_per_month} />
          <Input label="Weekly Price" type="number" placeholder="3,000"
            value={form.price_per_week} onChange={e => set('price_per_week', e.target.value)} />
          <Input label="Daily Price" type="number" placeholder="500"
            value={form.price_per_day} onChange={e => set('price_per_day', e.target.value)} />
        </div>
        <div className="grid-2" style={{ marginTop: 14 }}>
          <Input label="Production / Install Fee (AED)" type="number" placeholder="0"
            value={form.production_fee} onChange={e => set('production_fee', e.target.value)}
            hint="Extra fee for printing and installation (if applicable)" />
          <Input label="Minimum Booking (days)" type="number" placeholder="7"
            value={form.minimum_booking_days} onChange={e => set('minimum_booking_days', e.target.value)} />
        </div>

        {/* Your earnings preview */}
        {form.price_per_month && (
          <div style={{ marginTop: 12, padding: 14, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 'var(--radius)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.88rem', color: '#15803D' }}>Your monthly payout (after 10% commission)</span>
            <span style={{ fontWeight: 800, color: '#15803D' }}>
              {(parseFloat(form.price_per_month || 0) * 0.9).toLocaleString()} AED
            </span>
          </div>
        )}
      </section>

      {/* Audience & Impressions */}
      <section>
        <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Audience & Reach
        </h4>
        <div className="grid-2">
          <Input label="Daily Impressions (estimate)" type="number" placeholder="50000"
            value={form.daily_impressions} onChange={e => set('daily_impressions', e.target.value)}
            hint="Avg. people who see this space per day" />
          <div></div>
        </div>
        <div style={{ marginTop: 14 }}>
          <Textarea label="Nearby Landmarks (one per line)" rows={3} placeholder="Dubai Mall (500m)&#10;DIFC Gate (1km)&#10;Metro Station (200m)"
            value={form.nearby_landmarks} onChange={e => set('nearby_landmarks', e.target.value)} />
        </div>
      </section>

      {/* Photos */}
      <section>
        <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Photos
        </h4>
        <FileUpload
          label="Upload Space Photos"
          accept="image/*"
          multiple
          onFiles={setPhotos}
          hint="Upload high-quality photos of the space and its surroundings. JPG, PNG up to 10MB each."
          preview
        />
      </section>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--gray-100)' }}>
        {onClose && <Button variant="outline" onClick={onClose}>Cancel</Button>}
        <Button loading={loading} onClick={handleSave} icon={<Plus size={16} />}>
          {initial?.id ? 'Update Space' : 'List Ad Space'}
        </Button>
      </div>
    </div>
  )
}

export function InventoryPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [spaces, setSpaces] = useState([])
  const [loading, setLoading] = useState(true)
  const [editSpace, setEditSpace] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  useEffect(() => { load() }, [])

  async function load() {
    const { data } = await supabase.from('ad_spaces').select('*').eq('owner_id', profile.id).order('created_at', { ascending: false })
    setSpaces(data || [])
    setLoading(false)
  }

  async function toggleAvailability(space) {
    await supabase.from('ad_spaces').update({ is_available: !space.is_available }).eq('id', space.id)
    setSpaces(prev => prev.map(s => s.id === space.id ? { ...s, is_available: !s.is_available } : s))
    toast.success(`Space marked as ${!space.is_available ? 'available' : 'unavailable'}`)
  }

  async function deleteSpace(id) {
    const { error } = await supabase.from('ad_spaces').delete().eq('id', id)
    if (error) { toast.error('Could not delete space'); return }
    setSpaces(prev => prev.filter(s => s.id !== id))
    toast.success('Space deleted')
  }

  return (
    <DashboardLayout title="My Inventory" actions={
      <Button icon={<Plus size={16} />} onClick={() => { setEditSpace(null); setShowForm(true) }}>Add New Space</Button>
    }>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={36} /></div>
      ) : spaces.length === 0 ? (
        <EmptyState
          icon={<Building2 size={36} />}
          title="No ad spaces yet"
          description="Start listing your billboards, digital screens, and other advertising assets to receive booking inquiries."
          action={<Button icon={<Plus size={16} />} onClick={() => setShowForm(true)}>Add Your First Space</Button>}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
          {spaces.map(space => (
            <div key={space.id} className="card card-elevated" style={{ overflow: 'hidden' }}>
              {/* Image */}
              <div style={{ aspectRatio: '16/9', background: 'var(--gray-200)', position: 'relative', overflow: 'hidden' }}>
                <img
                  src={space.photos?.[0] || 'https://images.unsplash.com/photo-1617550523898-b2b67b3d5b16?w=400&q=80'}
                  alt={space.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{ position: 'absolute', top: 10, right: 10 }}>
                  <button
                    onClick={() => toggleAvailability(space)}
                    style={{
                      background: space.is_available ? '#D1FAE5' : '#FEE2E2',
                      color: space.is_available ? '#065F46' : '#991B1B',
                      border: 'none', borderRadius: 99, padding: '4px 10px',
                      fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4
                    }}
                  >
                    {space.is_available ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                    {space.is_available ? 'Available' : 'Unavailable'}
                  </button>
                </div>
              </div>

              <div style={{ padding: 16 }}>
                <div style={{ marginBottom: 4 }}>
                  <Badge variant="neutral" style={{ textTransform: 'capitalize', fontSize: '0.72rem' }}>{space.type?.replace('_', ' ')}</Badge>
                  {space.is_digital && <Badge variant="info" style={{ marginLeft: 6, fontSize: '0.72rem' }}>Digital</Badge>}
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4, fontSize: '1rem' }}>{space.title}</h3>
                <div style={{ fontSize: '0.82rem', color: 'var(--gray-400)', marginBottom: 12, display: 'flex', gap: 4 }}>
                  <MapPin size={12} style={{ marginTop: 2 }} /> {space.area}
                </div>

                <div style={{ display: 'flex', gap: 16, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--gray-100)' }}>
                  <div>
                    <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--navy)' }}>{space.price_per_month?.toLocaleString()} AED</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>/month</div>
                  </div>
                  {space.daily_impressions && (
                    <div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--tech)' }}>{(space.daily_impressions / 1000).toFixed(0)}k</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>daily views</div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" size="sm" icon={<Eye size={14} />} onClick={() => navigate(`/spaces/${space.id}`)}>Preview</Button>
                  <Button variant="outline" size="sm" icon={<Edit2 size={14} />} onClick={() => { setEditSpace(space); setShowForm(true) }}>Edit</Button>
                  <Button variant="ghost" size="sm" icon={<Trash2 size={14} />} onClick={() => setDeleteId(space.id)} style={{ color: 'var(--danger)', marginLeft: 'auto' }}>Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editSpace ? 'Edit Ad Space' : 'List New Ad Space'} size="xl">
        <SpaceForm
          initial={editSpace}
          onSave={() => { setShowForm(false); load() }}
          onClose={() => setShowForm(false)}
        />
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteSpace(deleteId)}
        title="Delete Ad Space"
        message="Are you sure you want to delete this ad space? This cannot be undone and will cancel any active bookings."
        confirmLabel="Delete"
        danger
      />
    </DashboardLayout>
  )
}

// Route-based new space form page
export function NewSpacePage() {
  const navigate = useNavigate()
  return (
    <DashboardLayout title="Add New Ad Space">
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div className="card" style={{ padding: 32 }}>
          <SpaceForm onSave={() => navigate('/inventory')} />
        </div>
      </div>
    </DashboardLayout>
  )
}
